/**
 * P-13 «Подбор» (изменение v1.12) — единственное место, где живёт всё про
 * подбор людей в «Движе».
 *
 * До v1.12 одно и то же было разложено по трём экранам и называлось
 * по-разному: «Подбор» (U-07, `/me/modes`) — в профиле внутри настроек,
 * «Фильтры» (P-03, `/dvizh/filters`) — панелью над колодой, «Пауза профиля»
 * (S-19) — строкой в настройках аккаунта. Первые два писали один и тот же
 * `PUT /me/modes/friends`, но один «сохранял», другой «применял»; пауза
 * давала ровно тот же результат, что выключенный подбор. Теперь это один
 * экран внутри вкладки «Движ», и попасть в него можно только оттуда.
 *
 * Композиция — «Discovery Settings» в Tinder и «Search settings» в Feeld:
 * статус анкеты сверху, один переключатель участия, дальше строки
 * «подпись — значение» с листами выбора вместо простыни чипов, «Сохранить»
 * в шапке (идиома форм «Движа»). ПС §08 «Настройки подбора», §09 «Фильтры».
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { RHYTHM_ICON, RHYTHM_LABEL, RHYTHM_ORDER } from '@entities/candidate/vibe'
import { describeModeHint, isModeReason, profileVisibility } from '@entities/user/visibility'
import { DISCOVERY_MODE, useDiscoveryStore } from '@features/discovery/discovery-store'
import {
  AGE_MAX,
  AGE_MIN,
  CITIES_MAX,
  FORMATS,
  TOPICS,
  describeMissing,
  fromUserMode,
  isSamePreferences,
  localMissing,
  pluralYears,
  summarize,
  summarizeRhythm,
  toModeBody,
  type ModePreferences,
} from '@features/discovery/mode-preferences'
import { CityPicker } from '@features/profile/CityPicker'
import { api, unwrap } from '@shared/api/client'
import type { City, ModeMissing } from '@shared/api/types'
import { Button, Chip, Dialog, DualRange, Icon, Sheet, Switch, cx, useToast, type IconName } from '@shared/ui'

import { Screen, useGoBack } from '../../layouts/Screen'
import { refreshMe } from '../../boot/bootstrap'
import { useAppStore } from '../../model/app-store'
import { VisibilityCard } from '../profile/VisibilityCard'
import styles from './setup.module.css'

/** Какой список выбираем прямо сейчас; `null` — ни один лист не открыт. */
type PickerId = 'cities' | 'topics' | 'formats' | 'rhythm'

/** Правка поверх сохранённого состояния; `null` — экран показывает сервер как есть. */
interface Draft {
  enabled: boolean
  value: ModePreferences
}

export function DiscoverySetupScreen(): React.ReactElement {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const toast = useToast()
  const me = useAppStore((s) => s.me)
  const invalidate = useDiscoveryStore((s) => s.invalidate)
  const mode = me?.modes.find((m) => m.mode === DISCOVERY_MODE) ?? null

  /*
   * Черновик отдельно от сохранённого: пока его нет, экран показывает то, что
   * пришло с сервера, и обновление `me` не стирает набранное. Раньше форма
   * жила в `useState` с `useEffect`-сбросом — и любое обновление `me` (а его
   * шлёт sync) могло затереть недосохранённые правки.
   */
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [picker, setPicker] = useState<PickerId | null>(null)

  const saved = mode ? fromUserMode(mode) : null

  if (!me || !mode || !saved)
    return (
      <Screen title="Подбор" back>
        {null}
      </Screen>
    )

  const enabled = draft?.enabled ?? mode.enabled
  const value = draft?.value ?? saved
  const dirty = draft !== null && (enabled !== mode.enabled || !isSamePreferences(value, saved))

  const edit = (changes: Partial<Draft>): void => setDraft({ enabled, value, ...changes })
  const change = (next: ModePreferences): void => edit({ value: next })

  const visibility = profileVisibility(me)
  // Незаполненное показываем по черновику: сервер о правках ещё не знает.
  const missing: ModeMissing[] = enabled ? localMissing(value) : []

  /*
   * Возврат к колоде — шагом назад по истории, а не новой записью: иначе
   * «Движ → Подбор → Движ» копится в стеке и системная «назад» возвращает
   * на экран, с которого человек только что ушёл. Записи нет (зашли по
   * прямой ссылке) — тогда обычный переход. Изменение v1.15: общий `useGoBack`
   * — он же правильно уходит из открытого диалога U-10.
   */
  const backToDeck = goBack

  const save = async (): Promise<void> => {
    setSaving(true)
    try {
      unwrap(
        await api.PUT('/me/modes/{mode}', {
          params: { path: { mode: mode.mode } },
          body: toModeBody(value, enabled) as never,
        }),
      )

      /*
       * Пауза профиля (S-19) выключала подбор вторым способом: пока она стоит,
       * ни колоды, ни показа анкеты. Отдельного переключателя для неё больше
       * нет — включая подбор, снимаем и её, иначе «включил, а ничего нет».
       * Своя ветка ошибки: параметры-то сохранились, и говорить «не удалось
       * сохранить» было бы неправдой — не удалось именно снять паузу.
       */
      let resumeFailed = false
      if (enabled && me.profile.paused) {
        try {
          await api.POST('/me/resume')
        } catch {
          resumeFailed = true
        }
      }

      await refreshMe()
      // Параметры изменились — прежняя колода им больше не соответствует.
      invalidate()
      setDraft(null)

      /*
       * Сохраняем всегда — набранное не теряется, — но к колоде возвращаем только
       * когда ей есть что показать. Без обязательного поля она ответит «заполните
       * параметры», и человек пойдёт обратно сюда: лучше остаться и дозаполнить.
       */
      if (resumeFailed) {
        // Статус анкеты под шапкой теперь покажет паузу и даст «Вернуться в подбор».
        toast.show('Параметры сохранены, но анкета осталась на паузе', { tone: 'danger' })
        return
      }

      if (missing.length) {
        toast.show(`Сохранено. Без этого подборки не будет: ${describeMissing(missing)}`)
        return
      }

      toast.show(enabled ? 'Параметры подбора сохранены' : 'Подбор выключен')
      backToDeck()
    } catch {
      toast.show('Не удалось сохранить параметры', { tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const cityNames = value.cities.map((city) => city.name)

  return (
    <Screen
      title="Подбор"
      back={backToDeck}
      // U-10: с несохранённым «назад» сначала спрашивает — экран не уезжает впустую
      backGuard={dirty ? () => setLeaveOpen(true) : null}
      narrow
      actions={
        <Button variant="ghost" size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
          Сохранить
        </Button>
      }
    >
      {/*
        Статус анкеты — теми же словами, что в профиле и над колодой, но только
        о том, что чинится не здесь: фото, пауза, модерация, санкция. Про
        выключенный подбор и пустые параметры говорят сам переключатель и
        подсвеченные строки ниже — карточка с кнопкой «Включить» уводила бы
        на экран, который уже открыт.
      */}
      {visibility.visible ? (
        <p className={styles['statusOk']}>
          <Icon name="check-circle" size={18} />
          Вас показывают подходящим людям
        </p>
      ) : isModeReason(visibility.state) ? null : (
        <VisibilityCard />
      )}

      <section className={styles['group']}>
        <div className={cx(styles['card'], styles['switchCard'])}>
          <Switch
            checked={enabled}
            onCheckedChange={(next) => edit({ enabled: next })}
            title="Искать людей в «Движе»"
            /* Одни и те же слова о статусе на всех экранах — entities/user/visibility */
            hint={describeModeHint(visibility, enabled)}
          />
        </div>
      </section>

      <section className={styles['group']}>
        <h2 className={styles['groupTitle']}>Кого показывать</h2>

        <div className={cx(styles['card'], !enabled && styles['cardMuted'])}>
          {/* Возраст — единственный параметр прямо в строке: у ползунка подписи свои */}
          <div className={styles['rangeRow']}>
            <span className={styles['rowLabel']}>Возраст</span>
            <DualRange
              value={value.age}
              onValueChange={(age) => change({ ...value, age })}
              min={AGE_MIN}
              max={AGE_MAX}
              disabled={!enabled}
              formatValue={(v) => `${v} ${pluralYears(v)}`}
              labelFrom="Возраст от"
              labelTo="Возраст до"
            />
          </div>

          <PickerRow
            label="Города"
            value={summarize(cityNames)}
            missing={missing.includes('cities')}
            disabled={!enabled}
            onClick={() => setPicker('cities')}
          />
          <PickerRow
            label="Темы"
            value={summarize(value.topics)}
            missing={missing.includes('topics')}
            disabled={!enabled}
            onClick={() => setPicker('topics')}
          />
          <PickerRow
            label="Форматы общения"
            value={summarize(value.formats)}
            missing={missing.includes('formats')}
            disabled={!enabled}
            onClick={() => setPicker('formats')}
          />
          <PickerRow
            label="Когда свободны"
            value={summarizeRhythm(value.rhythm, 'Всё равно')}
            disabled={!enabled}
            onClick={() => setPicker('rhythm')}
          />
        </div>

        <p className={styles['footnote']}>
          {missing.length
            ? `Без этого подборки не будет — не хватает: ${describeMissing(missing)}.`
            : 'Вас показывают друг другу, когда списки городов пересекаются. Искать по адресу, телефону или другим скрытым данным нельзя.'}
        </p>
      </section>

      <section className={styles['group']}>
        <div className={styles['card']}>
          <button
            type="button"
            className={cx(styles['row'], styles['rowPlain'])}
            onClick={() => void navigate({ to: '/me/preview' })}
          >
            <span className={styles['rowLabel']}>Как меня видят в подборке</span>
            <span className={styles['rowChevron']}>
              <Icon name="chevron-right" size={18} />
            </span>
          </button>
        </div>
        <p className={styles['footnote']}>
          Подбор не показывает ваш номер телефона и точное местоположение — только то, что видно в анкете.
        </p>
      </section>

      {/* --- Листы выбора: по одному списку за раз --- */}

      <CitiesPicker
        open={picker === 'cities'}
        cities={value.cities}
        homeCityId={me.profile.city?.id ?? null}
        onChange={(cities) => change({ ...value, cities })}
        onClose={() => setPicker(null)}
      />

      <ChipsSheet
        open={picker === 'topics'}
        title="Темы"
        description="О чём вам интересно с человеком"
        options={TOPICS.map((topic) => ({ id: topic, label: topic }))}
        selected={value.topics}
        onToggle={(topic) => change({ ...value, topics: toggle(value.topics, topic) })}
        onClose={() => setPicker(null)}
      />

      <ChipsSheet
        open={picker === 'formats'}
        title="Форматы общения"
        description="Чем вы готовы заняться вместе"
        options={FORMATS.map((format) => ({ id: format, label: format }))}
        selected={value.formats}
        onToggle={(format) => change({ ...value, formats: toggle(value.formats, format) })}
        onClose={() => setPicker(null)}
      />

      <ChipsSheet
        open={picker === 'rhythm'}
        title="Когда свободны"
        description="Необязательно: помогает совпасть по времени"
        options={RHYTHM_ORDER.map((slot) => ({
          id: slot,
          label: RHYTHM_LABEL[slot],
          icon: RHYTHM_ICON[slot],
        }))}
        selected={value.rhythm}
        onToggle={(slot) => change({ ...value, rhythm: toggle(value.rhythm, slot) })}
        onClose={() => setPicker(null)}
      />

      {/* --- U-10 «Несохранённые изменения» --- */}
      <Dialog
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        title="Не сохранять изменения?"
        description="Вы изменили параметры подбора, но не сохранили."
        footer={
          <>
            <Button variant="ghost" onClick={() => setLeaveOpen(false)}>
              Остаться
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                // Диалог закрывается тем же шагом истории — без второго back()
                setDraft(null)
                backToDeck()
              }}
            >
              Не сохранять
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setLeaveOpen(false)
                void save()
              }}
            >
              Сохранить
            </Button>
          </>
        }
      />
    </Screen>
  )
}

function toggle<T>(list: readonly T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
}

/* ===================================================================== */
/* Строка-переход «подпись — значение»                                    */
/* ===================================================================== */

function PickerRow({
  label,
  value,
  missing = false,
  disabled,
  onClick,
}: {
  label: string
  value: string
  /** Обязательное поле пустое — значение подсвечено, а не молча серое. */
  missing?: boolean
  disabled: boolean
  onClick: () => void
}): React.ReactElement {
  return (
    <button type="button" className={styles['row']} disabled={disabled} onClick={onClick}>
      <span className={styles['rowLabel']}>{label}</span>
      <span className={cx(styles['rowValue'], missing && styles['rowValueMissing'])}>{value}</span>
      <span className={styles['rowChevron']}>
        <Icon name="chevron-right" size={18} />
      </span>
    </button>
  )
}

/* ===================================================================== */
/* Листы выбора                                                           */
/* ===================================================================== */

interface ChipOption<T extends string> {
  id: T
  label: string
  icon?: IconName
}

/** Один список чипов на весь лист: выбор виден целиком и закрывается жестом. */
function ChipsSheet<T extends string>({
  open,
  title,
  description,
  options,
  selected,
  onToggle,
  onClose,
}: {
  open: boolean
  title: string
  description: string
  options: readonly ChipOption<T>[]
  selected: readonly T[]
  onToggle: (id: T) => void
  onClose: () => void
}): React.ReactElement {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <Button variant="primary" onClick={onClose}>
          Готово
        </Button>
      }
    >
      <div className={styles['chips']}>
        {options.map((option) => (
          <Chip
            key={option.id}
            icon={option.icon}
            selected={selected.includes(option.id)}
            onClick={() => onToggle(option.id)}
          >
            {option.label}
          </Chip>
        ))}
      </div>
    </Sheet>
  )
}

/**
 * Города подбора (изменение v1.5). Геопозицию клиент не передаёт, поэтому
 * география — это список городов: свой (снять нельзя — он в профиле) и те,
 * где человек бывает. Двое видят друг друга, когда списки пересекаются.
 */
function CitiesPicker({
  open,
  cities,
  homeCityId,
  onChange,
  onClose,
}: {
  open: boolean
  cities: readonly City[]
  homeCityId: string | null
  onChange: (cities: City[]) => void
  onClose: () => void
}): React.ReactElement {
  const [adding, setAdding] = useState(false)

  const add = (city: City): void => {
    if (cities.some((c) => c.id === city.id)) return
    onChange([...cities, city].slice(0, CITIES_MAX))
  }

  return (
    <>
      <Sheet
        open={open && !adding}
        onClose={onClose}
        title="Города"
        description="Вас показывают друг другу, если списки городов пересекаются"
        footer={
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        }
      >
        <div className={styles['chips']}>
          {cities.map((city) =>
            city.id === homeCityId ? (
              <Chip key={city.id} icon="pin" selected static>
                {city.name}
              </Chip>
            ) : (
              <Chip
                key={city.id}
                icon="close"
                selected
                aria-label={`Убрать город ${city.name}`}
                onClick={() => onChange(cities.filter((c) => c.id !== city.id))}
              >
                {city.name}
              </Chip>
            ),
          )}
          {cities.length < CITIES_MAX ? (
            <Chip icon="plus" onClick={() => setAdding(true)}>
              Добавить город
            </Chip>
          ) : null}
        </div>

        {!cities.length ? <p className={styles['sheetHint']}>Выберите хотя бы один город.</p> : null}
      </Sheet>

      {/* O-08: тот же лист выбирает город профиля */}
      <CityPicker
        open={adding}
        selectedId={null}
        selectedIds={cities.map((c) => c.id)}
        title="Добавить город"
        onSelect={add}
        onClose={() => setAdding(false)}
      />
    </>
  )
}
